import Tableview from '../../components/commos/Tableview'
import NavigationHeader from '../../components/navigation/NavigationHeader';
import Loading from '../../features/sso-auth/components/Loading';
import { rulesService, type RuleLegal, type RuleListItem } from '../../services/rulesService';
import { useCallback, useEffect, useState } from 'react';
import { useRulesStore } from '../../stores';
import { Button } from '@ab-inbev-maz/compass';
import { useNavigate } from 'react-router-dom';
import { useAlertStore } from '../../stores/alertStore';

const TablePage = () => {
  const [datos, setDatos] = useState<RuleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { getListRules } = useRulesStore();
  const showAlert = useAlertStore(state => state.showAlert);

  const navigate = useNavigate();
  const handleCreateRules = () => navigate('/rules');

  // Debounce para la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    const fetchRules = async () => {
      try {
        setLoading(true);
        const response = await getListRules({
          page: currentPage,
          page_size: pageSize,
          search: debouncedSearch || undefined
        });
        setDatos(response.results);
        setTotalCount(response.count);
      } catch (err) {
        console.error('Error fetching Rules:', err);
      } finally {
        setLoading(false);
      }
    };

    void fetchRules();
  }, [currentPage, pageSize, debouncedSearch]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1); // Reset a página 1 cuando se busca
  };

  const handleDownload = useCallback(async (selectedIds: (string | number)[]) => {
    try {
      const ruleIds = selectedIds.length > 0 ? selectedIds : undefined;
      const exportResult = await rulesService.exportRules(ruleIds);

      if (!exportResult.success || !exportResult.blob) {
        showAlert({
          text: 'Error al iniciar descarga',
          message: exportResult.message || 'No se pudo iniciar la exportación',
          status: 'error',
        });
        return;
      }

      const url = URL.createObjectURL(exportResult.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'rules_export.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      showAlert({
        text: 'Descarga completada',
        message: ruleIds
          ? `Se descargaron ${ruleIds.length} reglas seleccionadas`
          : 'Se descargaron todas las reglas del país',
        status: 'success',
      });
    } catch (error) {
      console.error('Error al descargar reglas:', error);
      showAlert({
        text: 'Error al descargar reglas',
        message: 'Ocurrió un error inesperado durante la descarga',
        status: 'error',
      });
    }
  }, [showAlert]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const columnas = [
    {
      key: 'name',
      title: 'Nombre',
    },

    {
      key: 'type',
      title: 'Tipo',
    },
    {
      key: 'template',
      title: 'Template',
    },
    {
      key: 'legal',
      title: 'Legal',
      render: (value: RuleLegal | null) => {
        if (!value) return '—';
        return (
          <div className="flex items-center gap-2">
            <span
              className="inline-block w-[10px] h-[10px] rounded-sm border border-gray-300 flex-shrink-0"
              style={{ backgroundColor: value.color }}
            />
            <span>{value.name}</span>
          </div>
        );
      },
    },
    {
      key: 'created_at',
      title: 'Fecha de creación',
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];
  // Mostrar loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-sm w-full space-y-8 bg-white p-8 rounded-3xl shadow-lg">
          <Loading message="Esto solo va tomar un momento, estamos cargando las reglas." />
        </div>
      </div>
    );
  }

  return (
    <>
      <NavigationHeader
        title='Librería de reglas'
        description='Desde aquí puedes consultar todas las reglas guardadas y crear nuevas cuando lo necesites.'
        backLink='/'
        actions={[
          <Button
            key="create-rules"
            label="Crear nuevas reglas"
            rightIcon="shopping/beer"
            variant="primary"
            onClick={handleCreateRules}
            className="cursor-pointer"
            data-testid="action-button"
          />
        ]}
      />
      <Tableview
        title='Librería de reglas'
        datos={datos}
        columnas={columnas}
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onDownload={handleDownload}
      />
    </>
  )
}

export default TablePage
